/**
 * Beschriftetes Eingabefeld.
 *
 * Die Beschriftung steht über dem Feld und ist per `htmlFor` verbunden —
 * ein Klick darauf setzt den Schreibzeiger. Bei einer Maske mit zwanzig
 * Feldern ist das kein Detail.
 */
import { useId, type ReactNode } from 'react';
import { alsCent, formatiereCent, istBetrag } from '../lib/geld';

/*
 * Die optionalen Felder tragen ausdrücklich `| undefined`.
 *
 * Mit exactOptionalPropertyTypes unterscheidet TypeScript zwischen „Feld
 * weggelassen" und „Feld ausdrücklich undefiniert". Für Daten, die an die
 * API gehen, ist die Unterscheidung wertvoll — ein versehentliches
 * `zahlungsdatum: undefined` soll auffallen. Bei Anzeigefeldern erzwingt
 * sie nur Ketten wie `{...(hinweis === undefined ? {} : { hinweis })}` an
 * jeder Aufrufstelle, ohne dass ein Fehler dadurch verhindert würde.
 */
type Gemeinsam = {
  beschriftung: string;
  pflicht?: boolean | undefined;
  hinweis?: string | undefined;
  fehler?: string | null | undefined;
  breit?: boolean | undefined;
};

export function Feld({
  beschriftung,
  pflicht,
  hinweis,
  fehler,
  breit,
  inhalt,
}: Gemeinsam & {
  /* Kein `children`: Das Feld gibt seine Kennung an das Eingabeelement
     weiter, damit die Beschriftung darauf zeigen kann. Ein Aufruf mit
     Argument ist etwas anderes als verschachteltes JSX — und heißt deshalb
     auch anders. */
  inhalt: (kennung: string) => ReactNode;
}): ReactNode {
  const kennung = useId();
  return (
    <div className={`feld${breit ? ' voll' : ''}`}>
      <label htmlFor={kennung}>
        {beschriftung}
        {pflicht ? <span className="pflicht"> *</span> : null}
      </label>
      {inhalt(kennung)}
      {hinweis ? <span className="hinweis">{hinweis}</span> : null}
      {fehler ? <span className="feldfehler">{fehler}</span> : null}
    </div>
  );
}

export function Textfeld({
  beschriftung,
  pflicht,
  hinweis,
  fehler,
  breit,
  wert,
  setzeWert,
  art = 'text',
  platzhalter,
  gesperrt,
  klasse,
  maxLaenge,
}: Gemeinsam & {
  wert: string;
  setzeWert: (wert: string) => void;
  art?: 'text' | 'email' | 'password' | 'date' | 'number' | 'tel' | 'url';
  platzhalter?: string;
  gesperrt?: boolean;
  klasse?: string;
  maxLaenge?: number;
}): ReactNode {
  return (
    <Feld
      beschriftung={beschriftung}
      pflicht={pflicht}
      hinweis={hinweis}
      fehler={fehler}
      breit={breit}
      inhalt={(kennung) => (
        <input
          id={kennung}
          type={art}
          value={wert}
          onChange={(e) => setzeWert(e.target.value)}
          disabled={gesperrt ?? false}
          className={`${klasse ?? ''}${fehler ? ' falsch' : ''}`.trim()}
          {...(platzhalter === undefined ? {} : { placeholder: platzhalter })}
          {...(maxLaenge === undefined ? {} : { maxLength: maxLaenge })}
        />
      )}
    />
  );
}

export function Auswahlfeld<T extends string>({
  beschriftung,
  pflicht,
  hinweis,
  fehler,
  breit,
  wert,
  setzeWert,
  optionen,
  leerText,
  gesperrt,
}: Gemeinsam & {
  wert: T | '';
  setzeWert: (wert: T) => void;
  optionen: readonly { wert: T; text: string }[];
  /** Erster Eintrag für „nichts gewählt". Fehlt er, ist die Auswahl zwingend. */
  leerText?: string;
  gesperrt?: boolean;
}): ReactNode {
  return (
    <Feld
      beschriftung={beschriftung}
      pflicht={pflicht}
      hinweis={hinweis}
      fehler={fehler}
      breit={breit}
      inhalt={(kennung) => (
        <select
          id={kennung}
          value={wert}
          onChange={(e) => setzeWert(e.target.value as T)}
          disabled={gesperrt ?? false}
          className={fehler ? 'falsch' : ''}
        >
          {leerText === undefined ? null : <option value="">{leerText}</option>}
          {optionen.map((o) => (
            <option key={o.wert} value={o.wert}>
              {o.text}
            </option>
          ))}
        </select>
      )}
    />
  );
}

export function Textbereich({
  beschriftung,
  pflicht,
  hinweis,
  fehler,
  breit,
  wert,
  setzeWert,
  zeilen = 3,
  platzhalter,
  gesperrt,
}: Gemeinsam & {
  wert: string;
  setzeWert: (wert: string) => void;
  zeilen?: number;
  platzhalter?: string;
  gesperrt?: boolean;
}): ReactNode {
  return (
    <Feld
      beschriftung={beschriftung}
      pflicht={pflicht}
      hinweis={hinweis}
      fehler={fehler}
      breit={breit}
      inhalt={(kennung) => (
        <textarea
          id={kennung}
          value={wert}
          rows={zeilen}
          onChange={(e) => setzeWert(e.target.value)}
          disabled={gesperrt ?? false}
          className={fehler ? 'falsch' : ''}
          {...(platzhalter === undefined ? {} : { placeholder: platzhalter })}
        />
      )}
    />
  );
}

/**
 * Betragseingabe mit Rückmeldung.
 *
 * Unter dem Feld steht, welchen Wert die Eingabe ergibt. Das ist kein
 * Schmuck: „1.500" ist zweideutig — deutsch gemeint tausendfünfhundert,
 * gelesen als eins Komma fünf (siehe `alsZahl` in api/src/lib/geld.ts).
 * Wer den verstandenen Betrag sieht, merkt den Unterschied vor dem
 * Speichern und nicht erst auf der Rechnung.
 */
export function Betragsfeld({
  beschriftung,
  pflicht,
  hinweis,
  breit,
  wert,
  setzeWert,
  gesperrt,
}: Gemeinsam & {
  wert: string;
  setzeWert: (wert: string) => void;
  gesperrt?: boolean;
}): ReactNode {
  const leer = wert.trim() === '';
  const gueltig = !leer && istBetrag(wert);
  const verstanden = gueltig ? formatiereCent(alsCent(wert)) : null;

  // Nur meckern, wenn wirklich etwas dasteht: Ein leeres Feld ist noch
  // kein Fehler, sondern ein unausgefülltes Formular.
  const fehler = !leer && !gueltig ? 'Kein gültiger Betrag. Beispiel: 1.234,56' : null;

  return (
    <Feld
      beschriftung={beschriftung}
      pflicht={pflicht}
      hinweis={verstanden ? `entspricht ${verstanden}` : hinweis}
      fehler={fehler}
      breit={breit}
      inhalt={(kennung) => (
        <input
          id={kennung}
          type="text"
          inputMode="decimal"
          value={wert}
          onChange={(e) => setzeWert(e.target.value)}
          disabled={gesperrt ?? false}
          className={`betrag${fehler ? ' falsch' : ''}`}
          placeholder="0,00"
        />
      )}
    />
  );
}

export function Ankreuzfeld({
  beschriftung,
  wert,
  setzeWert,
  gesperrt,
}: {
  beschriftung: string;
  wert: boolean;
  setzeWert: (wert: boolean) => void;
  gesperrt?: boolean;
}): ReactNode {
  const kennung = useId();
  return (
    <label className="ankreuz" htmlFor={kennung}>
      <input
        id={kennung}
        type="checkbox"
        checked={wert}
        disabled={gesperrt ?? false}
        onChange={(e) => setzeWert(e.target.checked)}
      />
      {beschriftung}
    </label>
  );
}
